import React, { useState } from 'react';
import { X, User, Calendar, Phone, Mail, CreditCard, Bed } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { cn } from '../utils/cn';

interface CreateBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomId: string;
  selectedDate: Date;
  onCreateBooking: (bookingData: CreateBookingRequest) => Promise<void>;
}

interface CreateBookingRequest {
  roomId: string;
  guestName: string;
  phone: string;
  email?: string;
  idProof?: string;
  checkinDate: string;
  checkoutDate: string;
  totalAmount: number;
  roomType: string;
  bookingSource: string;
}

const CreateBookingModal: React.FC<CreateBookingModalProps> = ({
  isOpen,
  onClose,
  roomId,
  selectedDate,
  onCreateBooking,
}) => {
  const [formData, setFormData] = useState({
    guestName: '',
    phone: '',
    email: '',
    idProof: '',
    checkinDate: format(selectedDate, 'yyyy-MM-dd'),
    checkoutDate: format(addDays(selectedDate, 1), 'yyyy-MM-dd'),
    totalAmount: 0,
    roomType: 'Standard',
    bookingSource: 'Direct',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.guestName.trim()) {
      newErrors.guestName = 'Guest name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9+\-\s()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.checkinDate) {
      newErrors.checkinDate = 'Check-in date is required';
    }

    if (!formData.checkoutDate) {
      newErrors.checkoutDate = 'Check-out date is required';
    }

    if (formData.checkinDate && formData.checkoutDate) {
      const checkin = new Date(formData.checkinDate);
      const checkout = new Date(formData.checkoutDate);
      
      if (checkout <= checkin) {
        newErrors.checkoutDate = 'Check-out date must be after check-in date';
      }
    }

    if (formData.totalAmount <= 0) {
      newErrors.totalAmount = 'Total amount must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await onCreateBooking({
        roomId,
        guestName: formData.guestName,
        phone: formData.phone,
        email: formData.email || undefined,
        idProof: formData.idProof || undefined,
        checkinDate: formData.checkinDate,
        checkoutDate: formData.checkoutDate,
        totalAmount: formData.totalAmount,
        roomType: formData.roomType,
        bookingSource: formData.bookingSource,
      });
      
      // Reset form
      setFormData({
        guestName: '',
        phone: '',
        email: '',
        idProof: '',
        checkinDate: format(selectedDate, 'yyyy-MM-dd'),
        checkoutDate: format(addDays(selectedDate, 1), 'yyyy-MM-dd'),
        totalAmount: 0,
        roomType: 'Standard',
        bookingSource: 'Direct',
      });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error creating booking:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        guestName: '',
        phone: '',
        email: '',
        idProof: '',
        checkinDate: format(selectedDate, 'yyyy-MM-dd'),
        checkoutDate: format(addDays(selectedDate, 1), 'yyyy-MM-dd'),
        totalAmount: 0,
        roomType: 'Standard',
        bookingSource: 'Direct',
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          body {
            overflow: hidden;
            margin: 0;
            padding: 0;
          }
        `}
      </style>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
        style={{ 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          margin: 0, 
          padding: '1rem',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
          width: '100vw',
          height: '100vh',
          zIndex: 9999
        }}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" 
          style={{ 
            margin: 0,
            backgroundColor: 'white',
            borderRadius: '1rem'
          }}
        >
          {/* Header */}
          <div className="bg-primary-500 text-white p-6 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Bed className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Create New Booking</h2>
                <p className="text-primary-100">Room {roomId} • {format(selectedDate, 'MMM dd, yyyy')}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
            <div className="space-y-6">
              {/* Guest Information */}
              <div className="glass-card p-4 rounded-lg">
                <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                  <User className="w-5 h-5 text-purple-600" />
                  <span>Guest Information</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Guest Name *
                    </label>
                    <input
                      type="text"
                      value={formData.guestName}
                      onChange={(e) => handleInputChange('guestName', e.target.value)}
                      className={cn(
                        "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                        errors.guestName ? "border-red-500" : "border-gray-300"
                      )}
                      placeholder="Enter guest name"
                      disabled={isLoading}
                    />
                    {errors.guestName && (
                      <p className="text-red-500 text-sm mt-1">{errors.guestName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={cn(
                        "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                        errors.phone ? "border-red-500" : "border-gray-300"
                      )}
                      placeholder="Enter phone number"
                      disabled={isLoading}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={cn(
                        "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                        errors.email ? "border-red-500" : "border-gray-300"
                      )}
                      placeholder="Enter email address"
                      disabled={isLoading}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ID Proof
                    </label>
                    <input
                      type="text"
                      value={formData.idProof}
                      onChange={(e) => handleInputChange('idProof', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter ID proof number"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              {/* Booking Details */}
              <div className="glass-card p-4 rounded-lg">
                <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <span>Booking Details</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Check-in Date *
                    </label>
                    <input
                      type="date"
                      value={formData.checkinDate}
                      onChange={(e) => handleInputChange('checkinDate', e.target.value)}
                      className={cn(
                        "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                        errors.checkinDate ? "border-red-500" : "border-gray-300"
                      )}
                      disabled={isLoading}
                    />
                    {errors.checkinDate && (
                      <p className="text-red-500 text-sm mt-1">{errors.checkinDate}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Check-out Date *
                    </label>
                    <input
                      type="date"
                      value={formData.checkoutDate}
                      onChange={(e) => handleInputChange('checkoutDate', e.target.value)}
                      className={cn(
                        "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                        errors.checkoutDate ? "border-red-500" : "border-gray-300"
                      )}
                      disabled={isLoading}
                    />
                    {errors.checkoutDate && (
                      <p className="text-red-500 text-sm mt-1">{errors.checkoutDate}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Room Type
                    </label>
                    <select
                      value={formData.roomType}
                      onChange={(e) => handleInputChange('roomType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={isLoading}
                    >
                      <option value="Standard">Standard</option>
                      <option value="Deluxe">Deluxe</option>
                      <option value="Suite">Suite</option>
                      <option value="Presidential">Presidential</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Booking Source
                    </label>
                    <select
                      value={formData.bookingSource}
                      onChange={(e) => handleInputChange('bookingSource', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={isLoading}
                    >
                      <option value="Direct">Direct</option>
                      <option value="MakeMyTrip">MakeMyTrip</option>
                      <option value="Booking.com">Booking.com</option>
                      <option value="Goibibo">Goibibo</option>
                      <option value="Walk-in">Walk-in</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Amount *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <CreditCard className="w-4 h-4 text-gray-400" />
                      </div>
                      <input
                        type="number"
                        value={formData.totalAmount}
                        onChange={(e) => handleInputChange('totalAmount', parseFloat(e.target.value) || 0)}
                        className={cn(
                          "w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                          errors.totalAmount ? "border-red-500" : "border-gray-300"
                        )}
                        placeholder="Enter total amount"
                        min="0"
                        step="0.01"
                        disabled={isLoading}
                      />
                    </div>
                    {errors.totalAmount && (
                      <p className="text-red-500 text-sm mt-1">{errors.totalAmount}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Bed className="w-4 h-4" />
                    <span>Create Booking</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateBookingModal;
